import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { createPortal } from 'react-dom';
import { GenericForm, GenericFormField, RequiredMark, GenericSelect } from '@/styles/GenericForm';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { VendorField } from '@/components/shared/VendorField';
import { FloatingAlert } from '@/components/ui/floating-alert';
import { Link } from 'react-router-dom';
import { BillForm, type BillDefaults } from '@/features/cost-management/bills/components/BillForm';
import { saveBill } from '@/features/cost-management/bills/services/billsService';
import { fetchPurchaseOrderBilling, fetchRelatedBills, type RelatedBill } from '@/features/cost-management/bills/services/billPurchaseOrdersService';
import { statusLabel } from '@/lib/status-label';
import { derivedDescription } from '@/lib/derived-description';
import { LinkedDocsSection, type LinkedDoc } from '@/components/shared/LinkedDocsSection';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StandardButton, CloseButton, SecondaryButton } from '@/styles/StandardButtons';
import { documentTotals, lineCostCodeProblem, linesTotal, parseNonNegative, parsePercent, type LineItem } from '@/lib/line-items';
import { fetchEntity, fetchEntityList } from '@/lib/entity-api';
import { fetchOffersByItem, type CatalogSearchOffer } from '../../catalog/services/catalog-api';
import { lineFromRow, linesProblem, repriceForVendor, toLinePayload } from '../lib/po-lines';
import { CatalogLineItems, useCatalogLines } from './CatalogLineItems';
import { colors } from '@/design-system/colors';
import { PurchaseOrder, NewPurchaseOrderInput, PurchaseOrderStatus } from '../types';
import { STATUS_OPTIONS, MAX_FILE_BYTES } from '../constants';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { useEntityComments } from '@/hooks/useEntityComments';
import { useProjectMembershipPrompt } from '@/hooks/useProjectMembershipPrompt';
import { fetchEntityAttachments } from '@/lib/entity-attachments';
import { getApiUrl } from '@/lib/api-config';
import { uploadPurchaseOrderAttachments, deletePurchaseOrderAttachment } from '../store';


/** Milestones are switched off in the UI for now. The stored data, the column and the code stay: flipping this brings the checkbox back. */
const SHOW_MILESTONES = false;

interface PurchaseOrderFormProps {
  purchaseOrder: PurchaseOrder | null;
  projects: { id: string; name: string }[];
  users: { id: string; name: string }[];
  vendors: { id: string; name: string }[];
  onClose: () => void;
  onSave: (data: NewPurchaseOrderInput) => Promise<PurchaseOrder | undefined>;
  isOpen: boolean;
  /** The purchase order was sent to the vendor (the server flipped it to Sent): the caller refreshes its list. */
  onSent?: () => void;
  /** Prefill for a NEW purchase order (e.g. created from a Warranty); ignored when editing. */
  defaults?: { title?: string; projectId?: string; description?: string; vendorId?: string; lines?: LineItem[]; taxPct?: string; otherAmount?: string };
}

export function PurchaseOrderForm({
  purchaseOrder,
  projects,
  users,
  vendors,
  onClose,
  onSave,
  isOpen,
  onSent,
  defaults,
}: PurchaseOrderFormProps) {
  const { companyId, user } = useUser();
  const { ensureMembers, prompt: membershipPrompt } = useProjectMembershipPrompt();
  const { selectedProject } = useProject();
  // "Create Bill": the Bill form (the same one the Bills page uses) opened with this purchase order's data
  const [billDefaults, setBillDefaults] = useState<BillDefaults | null>(null);
  const [createdBill, setCreatedBill] = useState<{ id: string; label: string } | null>(null);
  const [vendorLabel, setVendorLabel] = useState<string | null>(null);
  // the bills linked to this purchase order (many-to-many), shown read-only with a link to each
  const [relatedBills, setRelatedBills] = useState<RelatedBill[]>([]);
  const loadRelatedBills = useCallback(async () => {
    if (!purchaseOrder?.id) { setRelatedBills([]); return; }
    try {
      setRelatedBills((await fetchRelatedBills([purchaseOrder.id]))[purchaseOrder.id] || []);
    } catch (e) {
      console.error('Error loading the related bills:', e);
    }
  }, [purchaseOrder?.id]);
  // the change order this order was made from (shown in Linked Docs)
  const [changeOrderDoc, setChangeOrderDoc] = useState<LinkedDoc | null>(null);
  useEffect(() => {
    setChangeOrderDoc(null);
    const changeOrderId = isOpen ? purchaseOrder?.changeOrderId : undefined;
    if (!changeOrderId) return undefined;
    let cancelled = false;
    fetchEntity<any>('change_order', changeOrderId)
      .then((co) => { if (!cancelled && co) setChangeOrderDoc({ id: co.id, type: 'Change Order', entityType: 'change_order', label: co.title ? `${co.co_number} — ${co.title}` : co.co_number, detail: statusLabel(co.status) }); })
      .catch((failure) => console.error('Error loading the change order of this purchase order:', failure));
    return () => { cancelled = true; };
  }, [isOpen, purchaseOrder?.changeOrderId]);
  // the material order this order was made from (shown in Linked Docs)
  const [materialOrderDoc, setMaterialOrderDoc] = useState<LinkedDoc | null>(null);
  useEffect(() => {
    setMaterialOrderDoc(null);
    const materialListId = isOpen ? purchaseOrder?.materialListId : undefined;
    if (!materialListId) return undefined;
    let cancelled = false;
    fetchEntity<any>('material_list', materialListId)
      .then((mo) => { if (!cancelled && mo) setMaterialOrderDoc({ id: mo.id, type: 'Material Order', entityType: 'material_order', label: mo.name ? `${mo.material_order_id} — ${mo.name}` : String(mo.material_order_id ?? ''), detail: statusLabel(mo.status) }); })
      .catch((failure) => console.error('Error loading the material order of this purchase order:', failure));
    return () => { cancelled = true; };
  }, [isOpen, purchaseOrder?.materialListId]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [milestonesEnabled, setMilestonesEnabled] = useState<boolean>(false);
  const [milestonesText, setMilestonesText] = useState<string>('');
  const { comments, addComment, deleteComment, refetch: reloadComments } = useEntityComments('purchase_order', purchaseOrder?.id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The order's items. `linesLoaded` is false while an existing order's items are being read (or could not be): then they are never
  // sent on save, so a failed read can never wipe them. `initialLines` is what was loaded, to know whether anything changed.
  const [lines, setLines] = useState<LineItem[]>([]);
  // Tax (%) and Other Amount of the order (text while typing); the server stores Total = subtotal of the items + tax + other
  const [taxPct, setTaxPct] = useState('');
  const [otherAmount, setOtherAmount] = useState('');
  const initialChargesRef = useRef<string>('|');
  const chargesKey = () => `${taxPct.trim()}|${otherAmount.trim()}`;
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesLoaded, setLinesLoaded] = useState(true);
  const [linesLoadError, setLinesLoadError] = useState<string | null>(null);
  const [vendorFieldKey, setVendorFieldKey] = useState(0);
  const catalog = useCatalogLines({ isOpen, lines, setLines, vendorId: (formData.vendor_id || null) as string | null, vendors });
  const { setOffersByItem, rememberOffersOf } = catalog;
  const initialLinesRef = useRef<string>('[]');
  const serializeLines = (list: LineItem[]) => JSON.stringify(list.map(({ key: _key, ...rest }) => rest));
  const uploadPurchaseOrderAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    const uploaded = await uploadPurchaseOrderAttachments(id, files, companyId ?? '');
    return uploaded.map(att => ({
      id: att.id,
      file_name: att.name,
      file_type: att.type,
      file_size: att.size || 0,
      file_url: att.url,
      created_at: att.createdAt || new Date().toISOString(),
      created_by: att.uploadedBy || '',
      created_by_user: undefined,
    }));
  }, [companyId]);
  const deletePurchaseOrderAttachmentFn = useCallback(async (attachmentId: string): Promise<boolean> => {
    if (!purchaseOrder?.id) return false;
    return deletePurchaseOrderAttachment(attachmentId);
  }, [purchaseOrder?.id]);
  const {
    pendingFiles,
    uploadedFiles: uploadedAttachments,
    setUploadedFiles: setUploadedAttachments,
    handleUpload: handleFileUploadBase,
    handleRemovePending: handleRemovePendingFile,
    handleDelete: handleDeleteAttachmentBase,
    resetPending: resetPendingFiles,
    uploadPending: uploadPendingFiles,
  } = useAttachmentStaging<GenericAttachment>({
    // This form defers ALL uploads (create AND edit) to Submit time --
    // unlike the rest of the app's forms, it never uploads immediately even
    // when editing an existing purchase order. Passing no entityId keeps
    // handleUpload always staging; uploadPendingFiles is called explicitly
    // at submit time instead (see handleSubmit below).
    entityId: undefined,
    uploadFn: uploadPurchaseOrderAttachmentsFn,
    deleteFn: deletePurchaseOrderAttachmentFn,
  });
  const handleFileUpload = useCallback(async (files: File[]) => {
    const tooLarge = files.find(f => f.size > MAX_FILE_BYTES);
    if (tooLarge) {
      setError('One or more files exceed 20MB limit.');
      return;
    }
    setError(null);
    await handleFileUploadBase(files);
  }, [handleFileUploadBase]);
  const handleDeleteAttachment = useCallback(async (attachmentId: string) => {
    const ok = await handleDeleteAttachmentBase(attachmentId);
    if (!ok) {
      setError('Failed to delete attachment. Please try again.');
    }
  }, [handleDeleteAttachmentBase]);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const previousIsOpenRef = useRef(false);
  const initialValuesRef = useRef<Record<string, any>>({});
  const initialMilestonesEnabledRef = useRef<boolean>(false);
  const initialMilestonesTextRef = useRef<string>('');

  // Normalize function for consistent comparison
  const normalize = (obj: Record<string, any>): Record<string, string> => {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v == null ? '' : String(v).trim()])
    );
  };

  // Initialize form data
  const initialValues = useMemo(() => {
    if (purchaseOrder?.id) {
      return {
        project_id: purchaseOrder.projectId || '',
        title: purchaseOrder.title || '',
        completion_date: purchaseOrder.receivedAt || '',
        vendor_id: purchaseOrder.vendorId || '',
        amount: purchaseOrder.amount || 0,
        description: purchaseOrder.description || '',
        status: purchaseOrder.status || 'draft',
      };
    }
    return {
      project_id: defaults?.projectId || getDefaultProjectIdForNewRecord(selectedProject),
      title: defaults?.title ?? '',
      completion_date: '',
      vendor_id: '',
      amount: 0,
      description: '',
      status: 'draft',
    };
  }, [purchaseOrder, selectedProject, defaults?.projectId, defaults?.title]);

  // Capture initial values when form opens
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      // Capture initial form data first, before setting state
      const currentInitialValues = purchaseOrder?.id ? {
        project_id: purchaseOrder.projectId || '',
        title: purchaseOrder.title || '',
        completion_date: purchaseOrder.receivedAt || '',
        vendor_id: purchaseOrder.vendorId || '',
        amount: purchaseOrder.amount || 0,
        description: purchaseOrder.description || '',
        status: purchaseOrder.status || 'draft',
      } : {
        project_id: defaults?.projectId || getDefaultProjectIdForNewRecord(selectedProject),
        title: defaults?.title ?? '',
        completion_date: '',
        vendor_id: defaults?.vendorId ?? '',
        amount: 0,
        description: defaults?.description ?? '',
        status: 'draft',
      };

      const normalizedInitial = normalize(currentInitialValues);
      initialValuesRef.current = normalizedInitial;
      setFormData(currentInitialValues);
      setVendorLabel(null);
      setCreatedBill(null);
      setRelatedBills([]);
      void loadRelatedBills();

      // Set milestones
      const initialMilestonesEnabled = Array.isArray(purchaseOrder?.milestones) && (purchaseOrder?.milestones || []).length > 0;
      const initialMilestonesText = (purchaseOrder?.milestones || []).join('\n');
      setMilestonesEnabled(initialMilestonesEnabled);
      setMilestonesText(initialMilestonesText);
      initialMilestonesEnabledRef.current = initialMilestonesEnabled;
      initialMilestonesTextRef.current = initialMilestonesText;

      // This form stays mounted between opens (isOpen just toggles a null
      // render), so a file staged before a cancelled create would otherwise
      // still be sitting in state the next time this opens.
      resetPendingFiles();

      // The order's items: none for a new order; an existing one's are read once
      // (a new order made from another document -- a change order -- starts with a copy of its items)
      const startLines = !purchaseOrder?.id && defaults?.lines ? defaults.lines : [];
      setLines(startLines);
      setOffersByItem(new Map());
      rememberOffersOf(startLines.map((l) => l.catalogItemId).filter((id): id is string => !!id));
      setLinesLoadError(null);
      initialLinesRef.current = '[]';
      // a new order made from another document starts with that document's charges (a material order's tax % and delivery / other)
      const openTax = purchaseOrder?.id ? (purchaseOrder.taxPct ? String(purchaseOrder.taxPct) : '') : (defaults?.taxPct ?? '');
      const openOther = purchaseOrder?.id ? (purchaseOrder.otherAmount ? String(purchaseOrder.otherAmount) : '') : (defaults?.otherAmount ?? '');
      setTaxPct(openTax); setOtherAmount(openOther);
      initialChargesRef.current = `${openTax}|${openOther}`;
      if (purchaseOrder?.id) {
        setLinesLoaded(false);
        setLinesLoading(true);
        fetchEntityList<any>('purchase_order_item', { filters: { purchase_order_id: purchaseOrder.id } })
          .then((rows) => {
            const loaded = [...rows].sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0)).map((row) => lineFromRow(row, purchaseOrder.vendorId));
            setLines(loaded);
            initialLinesRef.current = serializeLines(loaded);
            setLinesLoaded(true);
            rememberOffersOf(loaded.map((l) => l.catalogItemId).filter((id): id is string => !!id));
          })
          .catch((failure) => {
            console.error('Error loading the purchase order items:', failure);
            setLinesLoadError('The items of this purchase order could not be loaded. They stay as they are: saving does not change them.');
          })
          .finally(() => setLinesLoading(false));
      } else {
        setLinesLoaded(true);
        setLinesLoading(false);
      }

      // Load attachments metadata (without signed URLs - lazy load those)
      if (purchaseOrder?.id) {
        const loadAttachments = async () => {
          try {
            setUploadedAttachments(await fetchEntityAttachments('purchase_order', purchaseOrder.id, { ascending: false }));
          } catch (error) {
            console.error('Error loading attachments:', error);
          }
        };

        loadAttachments();
        reloadComments();
      } else {
        setUploadedAttachments([]);
      }
    }
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleFieldChange = (name: string, value: any, updatedData?: Record<string, any>) => {
    const updated = updatedData || { ...formData, [name]: value };
    setFormData(updated);
  };

  const handleSubmit = async (data: Record<string, any>, skipClose: boolean = false) => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      setError(null);

      // Validate required fields
      if (!data.project_id || !data.title) {
        setError('Please fill in all required fields');
        setIsSubmitting(false);
        return;
      }
      // the vendor must belong to the project: asked once, now that the order is being saved
      if (!(await ensureMembers(data.project_id, [{ id: data.vendor_id || '', name: vendorDisplayName }]))) {
        setIsSubmitting(false);
        return;
      }

      // The items: every one must be saveable (checked here, never while the user is still preparing a line)
      if (linesLoaded) {
        const problem = linesProblem(lines);
        if (problem) { setError(problem); setIsSubmitting(false); return; }
        // every line that is going to be saved has a cost code (the shared item-table rule). Items nobody touched are not resent, so an old order
        // with a line from before this rule can still be edited -- the line is only asked for its code when it is saved again.
        if (!purchaseOrder?.id || serializeLines(lines) !== initialLinesRef.current || chargesKey() !== initialChargesRef.current) {
          const codeProblem = lineCostCodeProblem(lines);
          if (codeProblem) { setError(codeProblem); setIsSubmitting(false); return; }
        }
      }

      if (lines.length > 0) {
        if (parsePercent(taxPct) === null) { setError('Tax must be a percentage between 0 and 100.'); setIsSubmitting(false); return; }
        if (otherAmount.trim() !== '' && parseNonNegative(otherAmount) === null) { setError('Other amount must be a number (0 or more).'); setIsSubmitting(false); return; }
      }

      let descriptionToSend: string | undefined = undefined;
      if (purchaseOrder?.id) {
        const initialDesc = (purchaseOrder?.description ?? '').trim();
        const currentDesc = (data.description ?? '').trim();
        if (currentDesc !== initialDesc) descriptionToSend = currentDesc || undefined;
      } else {
        descriptionToSend = (data.description ?? '').trim() || undefined;
      }

      const payload: NewPurchaseOrderInput = {
        title: data.title,
        projectId: data.project_id,
        // an existing purchase order whose vendor was cleared sends null (undefined would leave the old vendor)
        vendorId: data.vendor_id || (purchaseOrder?.id ? null : undefined),
        completionDate: data.completion_date || undefined,
        // with items the amount is THEIR sum (the server computes the same number and is the one that stores it); without, the typed amount
        amount: lines.length > 0 ? documentTotals(linesTotal(lines), taxPct, otherAmount).total : Number(data.amount || 0),
        // Tax and Other Amount travel with the items; the server computes and stores the total (subtotal + tax + other)
        ...(lines.length > 0 ? { taxPct: parsePercent(taxPct) ?? 0, otherAmount: parseNonNegative(otherAmount) ?? 0 } : {}),
        // the items travel with the order in the same request; an existing order's are sent only when they changed
        lines: linesLoaded && (!purchaseOrder?.id || serializeLines(lines) !== initialLinesRef.current || chargesKey() !== initialChargesRef.current) ? toLinePayload(lines) : undefined,
        description: descriptionToSend,
        status: data.status as PurchaseOrderStatus,
        // the cost codes live on the items now: the order's own cost code is neither shown nor sent (what is stored is left alone)
        // milestones are hidden for now: an existing order's stay exactly as they are
        milestones: SHOW_MILESTONES ? (milestonesEnabled ? milestonesText.split(/\r?\n/).map(s => s.trim()).filter(Boolean) : []) : undefined,
        attachments: [],
      };

      const result = await onSave(payload);
      if (!result) throw new Error('Failed to save the purchase order');
      // what is on screen is what was saved: a later save compares against it
      initialLinesRef.current = serializeLines(lines);
      initialChargesRef.current = chargesKey();
      
      // Upload pending files after save
      if (result?.id) {
        await uploadPendingFiles(result.id);
      }

      if (!skipClose) {
        onClose();
      }

      return result;
    } catch (err: any) {
      console.error('Error saving purchase order:', err);
      setError(err?.message || 'Failed to save purchase order');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportPDF = async () => {
    let purchaseOrderId = purchaseOrder?.id;
    
    // Always save changes first (for both new and existing purchase orders)
    try {
      const savedPO = await handleSubmit(formData, true); // Pass true to skipClose
      if (savedPO && (savedPO as any).id) {
        purchaseOrderId = (savedPO as any).id;
      } else {
        setError('Please save the purchase order first before exporting PDF');
        return;
      }
    } catch (err: any) {
      console.error('Error saving purchase order before export:', err);
      setError(err?.message || 'Failed to save purchase order');
      return;
    }

    if (!purchaseOrderId || !companyId) {
      setError('Cannot export PDF: Missing purchase order or company information');
      return;
    }

    setIsGeneratingPDF(true);
    setError(null);

    try {
      // Send/Export separation (2026-09-14): Export PDF no longer goes
      // through document sharing (POST /share) -- it never needed a
      // persisted snapshot/share token, only the live document data to
      // print client-side. Opens the authenticated preview page, which
      // triggers window.print() itself (?print=1).
      window.open(`/documents/purchase_order/${purchaseOrderId}/preview?print=1`, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error('Error exporting PDF:', err);
      setError(err.message || 'Failed to export PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleSendToVendor = async () => {
    let purchaseOrderId = purchaseOrder?.id;

    // Nobody to send it to: say so before saving or creating anything
    if (!formData.vendor_id) {
      setError('Select a vendor before sending the purchase order.');
      return;
    }
    
    // Always save changes first (for both new and existing purchase orders)
    try {
      const savedPO = await handleSubmit(formData, true); // Pass true to skipClose
      if (savedPO && (savedPO as any).id) {
        purchaseOrderId = (savedPO as any).id;
      } else {
        setError('Please save the purchase order first before sending');
        return;
      }
    } catch (err: any) {
      console.error('Error saving purchase order before send:', err);
      setError(err?.message || 'Failed to save purchase order');
      return;
    }

    if (!purchaseOrderId || !companyId) {
      setError('Cannot send purchase order: Missing purchase order or company information');
      return;
    }

    setIsSendingEmail(true);
    setError(null);

    try {
      // Recipient (vendor) is resolved by the document definition from the
      // purchase order itself -- same "one click, no email input" UX as
      // before. Status update to 'sent' happens server-side after a
      // successful send (document-sharing/types/purchase-order.js).
      const apiUrl = getApiUrl(`api/documents/purchase_order/${purchaseOrderId}/share`);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to send purchase order';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.success) {
        if (!result.emailed) {
          throw new Error('No vendor email found for this purchase order');
        }
        alert('Purchase order sent successfully to vendor!');

        // The server flipped the status to Sent after the save: the list has to read it again
        onSent?.();
        onClose();
      }
    } catch (err: any) {
      console.error('Error sending purchase order:', err);
      setError(err.message || 'Failed to send purchase order');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // the "Bill created" notice leaves by itself (it floats over the form's buttons)
  useEffect(() => {
    if (!createdBill) return undefined;
    const timer = setTimeout(() => setCreatedBill(null), 10000);
    return () => clearTimeout(timer);
  }, [createdBill]);

  const vendorDisplayName = vendorLabel ?? vendors.find((v) => v.id === formData.vendor_id)?.name ?? '';

  // "Create Bill": the Bill form opens with a PHOTOGRAPH of this purchase order -- project, vendor, title, description and a copy of its lines (item,
  // description, cost code, unit, quantity, price, catalog item, and which order line each one covers) -- never the order's own rows, so editing the
  // bill cannot change the order. The bill starts billing what is still pending on every line, with the order's tax rate and the other charges not
  // billed yet; the user adjusts all of it to the vendor's real invoice. The order is saved first (its lines must exist to be copied).
  // The bill is saved already linked to the order (bill_purchase_orders), and a second bill for the same order is normal (progress billing).
  const handleCreateBill = async () => {
    if (!purchaseOrder?.id) return;
    if (createdBill && !window.confirm(`A bill (${createdBill.label}) was already created from this purchase order. Create another one?`)) return;
    try {
      const saved = await handleSubmit(formData, true);
      if (!saved) return;
      const billing = await fetchPurchaseOrderBilling(purchaseOrder.id);
      const title = (formData.title || '').trim();
      const poLines = billing.lines.map((line) => ({
        description: line.item_name,
        quantity: Number(line.quantity),
        unit_price: Number(line.unit_price ?? 0),
        unit: line.unit,
        cost_code_id: line.cost_code_id,
        catalog_item_id: line.catalog_item_id,
        purchase_order_item_id: line.id,
        bill_amount: line.pending,
      }));
      // an older order with no items: one line for what is left of its amount
      const leftOfAmount = Math.max(0, Number(formData.amount || 0) - billing.billed.total);
      setBillDefaults({
        project_id: formData.project_id || '',
        vendor_id: formData.vendor_id || '',
        vendor_name: vendorDisplayName,
        name: title,
        description: derivedDescription({ type: 'Purchase Order', number: purchaseOrder.poNumber, title, description: formData.description }),
        lines: poLines.length > 0 ? poLines : (leftOfAmount > 0 ? [{ description: title || `Purchase Order ${purchaseOrder.poNumber}`, quantity: 1, unit_price: leftOfAmount }] : []),
        ...(poLines.length > 0 ? { tax_pct: billing.purchase_order.tax_pct, other_amount: billing.pending.other } : {}),
        purchase_orders: [{ id: purchaseOrder.id, label: title ? `${purchaseOrder.poNumber} — ${title}` : purchaseOrder.poNumber }],
      });
    } catch (err: any) {
      console.error('Error preparing the bill:', err);
      setError(err?.message || 'The bill could not be prepared from this purchase order');
    }
  };

  const currentVendorId = formData.vendor_id || null;
  // The vendor of the order changed. Every item picked from the catalog is priced again with the NEW vendor's offer; an item the new vendor
  // has no offer for stays on the list at price 0 with no vendor. Prices the user typed by hand are only replaced after they agree. Nothing is removed
  // and no quantity changes. Declining (or a failed price check) leaves the order exactly as it was, vendor included.
  const handleVendorChange = async ({ id, name }: { id: string | null; name: string }) => {
    const nextVendorId = id || '';
    if (nextVendorId === (formData.vendor_id || '')) return;
    const applyVendor = (nextLines?: LineItem[]) => {
      setFormData((previous) => ({ ...previous, vendor_id: nextVendorId }));
      setVendorLabel(name);
      if (nextLines) setLines(nextLines);
    };
    const catalogIds = Array.from(new Set(lines.map((line) => line.catalogItemId).filter((value): value is string => !!value)));
    if (catalogIds.length === 0) { applyVendor(); return; }
    try {
      const offers = await fetchOffersByItem(catalogIds);
      setOffersByItem((previous) => { const next = new Map(previous); catalogIds.forEach((id) => next.set(id, (offers.get(id) ?? []) as unknown as CatalogSearchOffer[])); return next; });
      const { lines: repriced, manualAffected } = repriceForVendor(lines, offers as unknown as Map<string, CatalogSearchOffer[]>, nextVendorId || null);
      if (manualAffected.length > 0) {
        const names = manualAffected.map((line) => line.name).join(', ');
        const confirmed = window.confirm(`You edited the price of: ${names}. Changing the vendor replaces ${manualAffected.length === 1 ? 'that price' : 'those prices'} with the new vendor's price, or sets ${manualAffected.length === 1 ? 'it' : 'them'} to 0 when the vendor has none. Continue?`);
        if (!confirmed) { setVendorFieldKey((key) => key + 1); return; }
      }
      applyVendor(repriced);
      setError(null);
    } catch (failure) {
      console.error('Error checking the new vendor prices:', failure);
      setError('The new vendor\'s prices could not be checked, so the vendor was not changed. Try again.');
      setVendorFieldKey((key) => key + 1);
    }
  };

  // Every edit goes through the shared handler; removing the last item hands the amount back to the typed field, starting from nothing (the old sum no longer describes anything)
  const handleLinesChange = (next: LineItem[]) => {
    if (lines.length > 0 && next.length === 0) setFormData((previous) => ({ ...previous, amount: '' }));
    catalog.changeLines(next);
  };

  // Is there anything on screen that is not saved? (closing asks; so does leaving for a linked document)
  const hasUnsavedChanges = () => {
    // Normalize current form data for comparison
    const normalizedFormData = normalize(formData);
    // initialValuesRef.current is already normalized, but normalize again to be safe
    const normalizedInitial = normalize(initialValuesRef.current);

    // Compare all keys to ensure we catch any differences
    const formDataKeys = Array.from(new Set([...Object.keys(normalizedFormData), ...Object.keys(normalizedInitial)]));
    let formDataChanged = false;
    for (const key of formDataKeys) {
      const currentVal = normalizedFormData[key] || '';
      const initialVal = normalizedInitial[key] || '';
      if (currentVal !== initialVal) {
        formDataChanged = true;
        break;
      }
    }

    // Check milestones
    const milestonesEnabledChanged = milestonesEnabled !== initialMilestonesEnabledRef.current;
    const milestonesTextChanged = milestonesText.trim() !== initialMilestonesTextRef.current.trim();
    const hasPendingFiles = pendingFiles.length > 0;

    const linesChanged = linesLoaded && (serializeLines(lines) !== initialLinesRef.current || chargesKey() !== initialChargesRef.current);
    return formDataChanged || milestonesEnabledChanged || milestonesTextChanged || hasPendingFiles || linesChanged;
  };

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges()) {
      if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const mode = purchaseOrder?.id ? 'edit' : 'create';

  // Purchase order fields (for reference, actual layout is in customLayout)
  const purchaseOrderFields = useMemo<GenericFormField[]>(() => [
    {
      name: 'project_id',
      label: 'Project',
      type: 'select',
      required: true,
      row: 1,
      colSpan: 1,
      options: projects.map(p => ({ label: p.name, value: p.id })),
      placeholder: 'Select',
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      row: 1,
      colSpan: 1,
      placeholder: 'Enter title',
    },
    {
      name: 'completion_date',
      label: 'Completion Date',
      type: 'date',
      row: 2,
      colSpan: 1,
    },
    {
      name: 'vendor_id',
      label: 'Vendor',
      type: 'select',
      row: 2,
      colSpan: 1,
      options: [
        { label: 'Select a vendor', value: '__none__' },
        ...vendors.map(v => ({ label: v.name, value: v.id })),
      ],
      placeholder: 'Select',
    },
    {
      name: 'amount',
      label: 'Amount ($)',
      type: 'number',
      row: 2,
      colSpan: 1,
      placeholder: '0.00',
      step: '0.01',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      row: 3,
      colSpan: 2, // Full width in a 2-column grid
      placeholder: 'Enter description',
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      row: 4,
      colSpan: 1,
      options: STATUS_OPTIONS.map(s => ({ label: s.label, value: s.value })),
      placeholder: 'Select',
    },
  ], [projects, vendors]);

  // Custom layout for purchase order form
  const customLayout = (
    <div
      className="generic-form-overlay"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleCloseAttempt();
        }
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          // a failure is already on screen (handleSubmit sets the error before rethrowing for the callers that need it)
          handleSubmit(formData).catch(() => undefined);
        }}
        className="generic-form-container"
        style={{ width: 'var(--form-width-default)', maxHeight: 'var(--form-max-height-default)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="generic-form-header generic-form-header-default"
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <h2 className="font-form-title">
            {purchaseOrder?.id ? 'Edit Purchase Order' : 'New Purchase Order'}
          </h2>
          <CloseButton onClick={handleCloseAttempt} />
        </div>

        {/* Body */}
        <div className="generic-form-body generic-form-body-default">
          {error && (
            <div className="generic-form-error">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Row 1: Title and Project (each half width) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="title" className="font-form-label">Title<RequiredMark required /></Label>
                <Input
                  id="title"
                  value={formData.title || ''}
                  onChange={(e) => {
                    const updated = { ...formData, title: e.target.value };
                    setFormData(updated);
                    handleFieldChange('title', e.target.value, updated);
                  }}
                  placeholder="Enter title"
                  className="font-form-input generic-form-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project_id" className="font-form-label">Project<RequiredMark required /></Label>
                <GenericSelect
                  id="project_id"
                  value={formData.project_id || ''}
                  onValueChange={(value) => {
                    const updated = { ...formData, project_id: value };
                    setFormData(updated);
                    handleFieldChange('project_id', value, updated);
                  }}
                  placeholder="Select"
                  required
                  options={projects.map(p => ({ label: p.name, value: p.id }))}
                />
              </div>
            </div>

            {/* Row 2: Completion Date, Vendor, Amount, Status (each 1/4 width) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="completion_date" className="font-form-label">Completion Date</Label>
                <Input
                  id="completion_date"
                  type="date"
                  value={formData.completion_date || ''}
                  onChange={(e) => {
                    const updated = { ...formData, completion_date: e.target.value };
                    setFormData(updated);
                    handleFieldChange('completion_date', e.target.value, updated);
                  }}
                  className="font-form-input generic-form-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor_id" className="font-form-label">Vendor</Label>
                <VendorField
                  key={vendorFieldKey}
                  id="vendor_id"
                  value={vendorDisplayName}
                  allowFreeText={false}
                  placeholder="Select vendor"
                  onChange={(selection) => { void handleVendorChange(selection); }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount" className="font-form-label">Amount ($)</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  // with items the amount is their sum (calculated, not typed); a pending-price item makes it provisional
                  readOnly={lines.length > 0}
                  title={lines.length > 0 ? 'Calculated from the items' : undefined}
                  value={lines.length > 0 ? documentTotals(linesTotal(lines), taxPct, otherAmount).total.toFixed(2) : (formData.amount || '')}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9.]/g, '');
                    const updated = { ...formData, amount: v };
                    setFormData(updated);
                    handleFieldChange('amount', v, updated);
                  }}
                  placeholder="0.00"
                  className="font-form-input generic-form-field [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e: React.WheelEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                    e.currentTarget.addEventListener('wheel', (event) => {
                      event.preventDefault();
                    }, { passive: false });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status" className="font-form-label">Status</Label>
                <GenericSelect
                  id="status"
                  value={formData.status || ''}
                  onValueChange={(value) => {
                    const updated = { ...formData, status: value };
                    setFormData(updated);
                    handleFieldChange('status', value, updated);
                  }}
                  placeholder="Select"
                  options={STATUS_OPTIONS.map(s => ({ label: s.label, value: s.value }))}
                />
              </div>
            </div>

            {/* Description (full width) */}
            <div className="space-y-2">
              <Label htmlFor="description" className="font-form-label">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => {
                  const updated = { ...formData, description: e.target.value };
                  setFormData(updated);
                  handleFieldChange('description', e.target.value, updated);
                }}
                rows={6}
                placeholder="Enter description"
              />
            </div>

            {/* Items: what the vendor is asked for -- search the catalog or add from it, edit quantity and price in place */}
            <CatalogLineItems
              catalog={catalog}
              lines={lines}
              vendorId={currentVendorId}
              vendors={vendors}
              onLinesChange={handleLinesChange}
              adjustments={{ taxPct, onTaxPctChange: setTaxPct, otherAmount, onOtherAmountChange: setOtherAmount }}
              loading={linesLoading}
              loadError={linesLoadError}
              disabled={!linesLoaded}
            />

            {/* Milestones: hidden for now (SHOW_MILESTONES); the data and the code stay */}
            {SHOW_MILESTONES && (
              <div className="space-y-2">
                <div className="flex items-end gap-2">
                  <input
                    id="milestonesToggle"
                    type="checkbox"
                    checked={milestonesEnabled}
                    onChange={(e) => setMilestonesEnabled(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="milestonesToggle" className="font-form-label">Enable Milestones</Label>
                </div>
                {milestonesEnabled && (
                  <>
                    <Label htmlFor="milestones" className="font-form-label">Milestones</Label>
                    <Textarea
                      id="milestones"
                      rows={4}
                      value={milestonesText}
                      onChange={(e) => setMilestonesText(e.target.value)}
                      placeholder="e.g. Mobilization&#10;Rough-in complete&#10;Final inspection"
                    />
                  </>
                )}
              </div>
            )}

            {/* Linked Docs: the bills linked to this order and the change order / material order it was made from -- shown, never edited here */}
            {purchaseOrder?.id && (
              <LinkedDocsSection
                emptyText="No documents are linked to this purchase order yet"
                hasUnsavedChanges={hasUnsavedChanges}
                docs={[
                  ...(changeOrderDoc ? [changeOrderDoc] : []),
                  ...(materialOrderDoc ? [materialOrderDoc] : []),
                  ...relatedBills.map((b) => ({
                    id: b.id,
                    type: 'Bill',
                    entityType: 'bill',
                    label: b.name ? `${b.bill_id} — ${b.name}` : b.bill_id,
                    detail: `${statusLabel(b.status)} · $${Number(b.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                  })),
                ]}
              />
            )}

            {/* Attachments Section */}
            <GenericAttachmentsSection
              attachments={uploadedAttachments}
              pendingFiles={pendingFiles}
              onUpload={handleFileUpload}
              onDelete={purchaseOrder?.id ? handleDeleteAttachment : undefined}
              onRemovePending={handleRemovePendingFile}
              projectId={formData.project_id || ''} mode={purchaseOrder?.id ? 'edit' : 'create'}
              disabled={false}
            />

            {/* Comments Section */}
            <GenericCommentsSection
              comments={comments}
              onAddComment={addComment}
              onDeleteComment={deleteComment}
              mode={purchaseOrder?.id ? 'edit' : 'create'}
              audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
              placeholder={purchaseOrder?.id ? 'Add a comment...' : 'Comments are available after creating the purchase order'}
            />
          </div>
        </div>

        {/* Footer */}
        <div 
          className="generic-form-footer generic-form-footer-default"
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <div className="flex gap-2">
            <StandardButton
              onClick={handleCloseAttempt}
              type="button"
              customVariant="white"
            >
              Cancel
            </StandardButton>
            <StandardButton
              type="button"
              customVariant="white"
              onClick={handleSendToVendor}
              disabled={isSendingEmail || isGeneratingPDF || isSubmitting}
            >
              {isSendingEmail ? 'Sending...' : 'Send to Vendor'}
            </StandardButton>
            <StandardButton
              type="button"
              customVariant="white"
              onClick={handleExportPDF}
              disabled={isGeneratingPDF || isSendingEmail || isSubmitting}
            >
              {isGeneratingPDF ? 'Generating...' : 'Export PDF'}
            </StandardButton>
            {purchaseOrder?.id && (
              <StandardButton
                type="button"
                customVariant="white"
                onClick={handleCreateBill}
                disabled={isGeneratingPDF || isSendingEmail || isSubmitting || !formData.project_id}
              >
                Create Bill
              </StandardButton>
            )}
            <SecondaryButton
              type="submit"
              disabled={isSubmitting || !formData.project_id || !formData.title}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </SecondaryButton>
          </div>
        </div>
      </form>
    </div>
  );

  if (!isOpen) return null;

  // Render in a portal to ensure it's at the document body level
  // This ensures fixed positioning works correctly and covers the entire screen
  return (
    <>
      {membershipPrompt}
      {createPortal(customLayout, document.body)}
      {/* The Bill form is a sibling of this form (not inside it): a submit inside it must never submit the purchase order */}
      <BillForm
        // a new bill made from this order (Save closes it like every form; the order stays open under it)
        bill={null}
        projects={projects}
        isOpen={billDefaults !== null}
        defaults={billDefaults ?? undefined}
        onClose={() => setBillDefaults(null)}
        onSaved={() => { void loadRelatedBills(); }}
        onSave={async (billData) => {
          if (!companyId || !user) return undefined;
          const saved = await saveBill(billData, { companyId: companyId as string, userId: user.id });
          if (!billData.id) setCreatedBill({ id: saved.id, label: (saved as any).bill_id || 'Bill' });
          return saved;
        }}
      />
      {createdBill && (
        <FloatingAlert title={`Bill ${createdBill.label} created`} description="It was created from this purchase order." onDismiss={() => setCreatedBill(null)}>
          <Link to={`/cost-management/bills?bill=${createdBill.id}`} className="underline">Open bill</Link>
        </FloatingAlert>
      )}
    </>
  );
}

